/**
 * One Resource Plan comment thread — list, composer, author-only remove.
 *
 * Built for RP-32.4's line notes (product decision 2026-10-17: one table, one API). It takes
 * a `scope` (`{ planLineId }` now, `{ concernId }` later) and renders what the server
 * returns: who may write and who may remove is the server's call, never this component's.
 * Whole-plan comments are posted and read in the plan's Activity tab (PlanActivityPanel),
 * which lists every comment beside the plan's Staffing Concerns.
 *
 * Flat and chronological — no nested replies, no editing. A removed note keeps its place as
 * "Note removed". @mentions are plain text until notifications exist.
 */

import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  type CommentScope,
  useAddPlanComment,
  useDeletePlanComment,
  usePlanComments,
} from "@/hooks/use-plan-comments";

const MAX = 2000;

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function CommentThread({
  planId,
  scope,
  enabled = true,
  emptyText = "No notes yet.",
}: {
  planId: string;
  scope: CommentScope & { planLineId: string };
  enabled?: boolean;
  emptyText?: string;
}) {
  const [draft, setDraft] = useState("");
  const { data, isLoading, isError, error, refetch } = usePlanComments(planId, scope, enabled);
  const add = useAddPlanComment(planId);
  const remove = useDeletePlanComment();
  const trimmed = draft.trim();

  return (
    <div className="flex flex-col gap-3">
      {isLoading && <p className="text-sm text-muted-foreground">Loading notes…</p>}
      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load the notes."}
          onRetry={() => refetch()}
        />
      )}
      {data !== undefined && data.data.length === 0 && (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      )}
      {data !== undefined && data.data.length > 0 && (
        <ol className="flex flex-col gap-2">
          {data.data.map((c) => (
            <li key={c.id} className="rounded-md border border-border bg-card px-3 py-2">
              <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                <span>
                  <span className="font-semibold text-foreground">
                    {c.author?.name ?? "Former user"}
                  </span>{" "}
                  · {when(c.createdAt)}
                </span>
                {c.canDelete && (
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(c.id)}
                    className="font-medium hover:text-foreground"
                  >
                    Remove
                  </button>
                )}
              </div>
              {c.body === null ? (
                <p className="mt-1 text-sm italic text-muted-foreground">
                  Note removed{c.deletedByName !== null && ` by ${c.deletedByName}`}
                </p>
              ) : (
                <p className="mt-1 whitespace-pre-wrap text-sm">{c.body}</p>
              )}
            </li>
          ))}
        </ol>
      )}

      {data?.canWrite === true && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmed === "") return;
            add.mutate(
              { planLineId: scope.planLineId, body: trimmed },
              { onSuccess: () => setDraft("") },
            );
          }}
        >
          <Textarea
            value={draft}
            maxLength={MAX}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Add a note — a consideration, a dependency, who to talk to…"
            aria-label="New note"
            rows={3}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              Internal only — never in the client export. {draft.length}/{MAX}
            </span>
            <Button type="submit" size="sm" disabled={trimmed === "" || add.isPending}>
              {add.isPending ? "Adding…" : "Add note"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
